'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

/**
 * Skin Lab gate (development and preview deployments only; switched off on leadpops.com since the design went live). Costs normal visitors one tiny effect and NOTHING else: the panel and
 * the skin stylesheet (a string injected at runtime, never a CSS import) are a separate chunk that only loads when someone
 * opens / or /mortgage-leads with ?skin=game.
 *   ?skin=game   turn the game skin on (remembered for this tab only)
 *   ?skin=off    turn it off
 *   &skinmode=light|dark   start in that mode (default dark)
 * The skin applies only on the pages in SKIN_PATHS. Elsewhere it stays dormant and comes back when you return.
 * Notes and the list of departures from the owner rulings: docs/skin/GAME-SKIN-NOTES.md
 */
const Panel = dynamic(() => import('./SkinLabPanel'), { ssr: false });
const SKIN_PATHS = new Set(['/', '/mortgage-leads']);

export function SkinLabGate() {
  const pathname = usePathname();
  const [on, setOn] = useState(false);
  useEffect(() => {
    try {
      // The game design is live (2026-10-01); the lab is a development tool only. Never on the live site, and clear any
      // flag a tab still carries from earlier preview visits.
      const host = window.location.hostname;
      if (host === 'leadpops.com' || host.endsWith('.leadpops.com')) { sessionStorage.removeItem('skinlab.on'); setOn(false); return; }
      const sp = new URLSearchParams(window.location.search);
      const q = sp.get('skin');
      if (q === 'off') sessionStorage.removeItem('skinlab.on');
      else if (q === 'game') sessionStorage.setItem('skinlab.on', '1');
      const m = sp.get('skinmode');
      if (m === 'light' || m === 'dark') sessionStorage.setItem('skinlab.mode', m);
      setOn(sessionStorage.getItem('skinlab.on') === '1');
    } catch { /* storage blocked: skin simply stays off */ }
  }, [pathname]);
  return on && SKIN_PATHS.has(pathname ?? '') ? <Panel onExit={() => setOn(false)} /> : null;
}
